/** @jsxImportSource @emotion/react */
'use client';

import React, { type KeyboardEvent, type MouseEvent } from 'react';
import { StyledIconButton } from './styled';
import type { IconButtonProps } from './types';

function blockDisabledActivation(e: MouseEvent | KeyboardEvent) {
  e.preventDefault();
  e.stopPropagation();
}

export const IconButton = React.forwardRef<HTMLButtonElement, IconButtonProps>(
  (
    {
      icon,
      'aria-label': ariaLabel,
      variant = 'ghost',
      size = 'md',
      disabled = false,
      type = 'button',
      as,
      href,
      onClick,
      className,
      testId = 'icon-button',
      css,
      ...rest
    },
    ref
  ) => {
    const isAnchor = as === 'a' || Boolean(href);
    const resolvedHref = isAnchor && !disabled ? href : isAnchor ? undefined : href;

    const handleClick = (e: MouseEvent<HTMLButtonElement>) => {
      if (disabled) {
        blockDisabledActivation(e);
        return;
      }
      onClick?.(e);
    };

    const handleKeyDown = (e: KeyboardEvent<HTMLButtonElement>) => {
      if (!disabled || !isAnchor) {
        return;
      }
      if (e.key === 'Enter' || e.key === ' ') {
        blockDisabledActivation(e);
      }
    };

    return (
      <StyledIconButton
        ref={ref}
        as={as ?? (href ? 'a' : undefined)}
        href={resolvedHref}
        onClick={handleClick as any}
        onKeyDown={handleKeyDown as any}
        disabled={isAnchor ? undefined : disabled}
        aria-disabled={disabled || undefined}
        aria-label={ariaLabel}
        tabIndex={isAnchor && disabled ? -1 : undefined}
        type={isAnchor ? undefined : type}
        data-testid={testId}
        data-variant={variant}
        $variant={variant}
        $size={size}
        className={className}
        css={css}
        {...rest}
      >
        <span aria-hidden="true">{icon}</span>
      </StyledIconButton>
    );
  }
);

IconButton.displayName = 'IconButton';
